//
// Copyright (c) 2011-2024 by Curalate, Inc
//

import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import {
  FlatDefaultIconButton,
  PrimaryLinkButton,
} from '@components/atoms/button/Button';
import { ContentImportOptions } from '@content-drawer/ContentImportOptions';
import { ContentDrawerTrackingIds } from '@content-drawer/contentDrawerConstants';
import { getContentDrawerSettings } from '@content-drawer/selectors/getContentDrawerSettings';
import { isDefined } from '@utils/filterPredicates';

export interface MediaPanelActionsProps {
  filtersCount: number;
  onClear: () => void;
  selectedItemsCount: number;
  toggleFilters: (setter: (state: boolean) => boolean) => void;
}

export const MediaPanelActions = ({
  filtersCount,
  onClear,
  selectedItemsCount,
  toggleFilters,
}: MediaPanelActionsProps) => {
  const drawerSettings = useSelector(getContentDrawerSettings);

  const hasMultiSelectLimit = isDefined(drawerSettings.multiSelectLimit);

  return (
    <Actions>
      <FiltersIconWrapper>
        <FlatDefaultIconButton
          iconName='filters'
          trackingId={ContentDrawerTrackingIds.TOGGLE_FILTERS}
          onClick={() => toggleFilters((value) => !value)}
        />
        {filtersCount > 0 && <FiltersCount>{filtersCount}</FiltersCount>}
      </FiltersIconWrapper>
      <ContentImportOptions triggerType='content-drawer-upload' />
      {hasMultiSelectLimit &&
        selectedItemsCount === 0 &&
        `Select up to ${drawerSettings.multiSelectLimit}`}
      {selectedItemsCount > 0 && (
        <span>
          <b>{selectedItemsCount}</b>
          {hasMultiSelectLimit && (
            <>
              &nbsp;of&nbsp;
              <b>{drawerSettings.multiSelectLimit}</b>
            </>
          )}
          &nbsp;selected
        </span>
      )}
      {selectedItemsCount > 1 && (
        <PrimaryLinkButton onClick={onClear}>Clear All</PrimaryLinkButton>
      )}
    </Actions>
  );
};

const Actions = styled.header(
  ({ theme }) => `
    display: flex;
    justify-content: start;
    align-items: center;
    gap: ${theme.spacings[3]};
    padding: ${theme.spacings[3]};
  `,
);

const FiltersIconWrapper = styled.div(
  () => `
    position: relative;
  `,
);

const FiltersCount = styled.span(
  ({ theme }) => `
    display: flex;
    width: calc(${theme.spacings[4]} - ${theme.spacings[1]});
    height: calc(${theme.spacings[4]} - ${theme.spacings[1]});
    justify-content: center;
    align-items: center;
    position: absolute;
    top: -${theme.spacings[1]};
    left: ${theme.spacings[3]};
    background-color: ${theme.colors.aliases.primaryShade1};
    border-radius: ${theme.borderRadii.pill};
    color: ${theme.colors.white};
  `,
);
